import { fetchExperienceListFacets } from 'actions/ExperienceActions'
import Places from 'constants/places'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject } from 'lib/array/arrayUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { useEffect, useMemo } from 'react'
import { type ExpListDefinition, useCategoryCarousels } from '../ExperiencesPage/ExperiencesPageHardcodedLists'
import useMostPopulatedSearchExperienceFacets from './useMostPopulatedSearchExperienceFacets'

type ExperienceThemeChildCategory = App.ExperienceItemCategory & { count: number }

export type ExperienceTheme =
  | { id: string; title: string; count: number; type: 'category'; categoryCode: number; children: Array<ExperienceThemeChildCategory> }
  | { id: string; title: string; count: number; type: 'collection'; collectionId: string }

const GLOBAL_FACET_KEY = getObjectKey({ placeId: Places.Anywhere.id })

const SEARCH_THEME_TITLES_BY_CATEGORY_CODE: Record<number, string> = {
  1: 'Wine & Dine',
  9: 'Top Activities',
  18: 'See the Sights',
  48: 'Day Tours',
}

export function getSearchThemeTitle(categoryCode: number, fallbackTitle: string): string {
  return SEARCH_THEME_TITLES_BY_CATEGORY_CODE[categoryCode] ?? fallbackTitle
}

function useExperienceThemes(place?: App.Place): Array<ExperienceTheme> {
  const dispatch = useAppDispatch()
  const searchFacets = useAppSelector((state) => state.experience.experienceListFacets)
  const { isAnyVariant: discoveryRevampEnabled } = useExperiment(
    Experiments.experiencesSearchDiscoveryRevamp,
  )
  const categoryCarousels = useCategoryCarousels()
  const {
    primaryCategories,
    categories: categoryFacets,
    collections: placeCollections,
  } = useMostPopulatedSearchExperienceFacets(place, {
    rankCategoriesByRelevance: discoveryRevampEnabled,
    prioritiseSelectedCategories: discoveryRevampEnabled,
    sendPerformanceVariant: discoveryRevampEnabled,
    includeCollectionsWithoutExperiment: discoveryRevampEnabled,
  })

  useEffect(() => {
    dispatch(fetchExperienceListFacets(Places.Anywhere.id, {}))
  }, [dispatch])

  const countByCode = useMemo(
    () => new Map(primaryCategories.map((item) => [item.parent.id, item.count])),
    [primaryCategories],
  )

  const categoryFacetsById = useMemo(
    () => arrayToObject(categoryFacets, (facet) => facet.categoryId),
    [categoryFacets],
  )

  const globalCollections = useMemo(() => {
    return searchFacets[GLOBAL_FACET_KEY]?.facets?.collection ?? []
  }, [searchFacets])

  const placeCollectionCountById = useMemo(
    () => new Map(placeCollections.map((collection) => [collection.collectionId, collection.count])),
    [placeCollections],
  )

  return useMemo(() => {
    const categoryCarouselsByCode = new Map(
      categoryCarousels
        .filter((carousel): carousel is ExpListDefinition & { code: number } => carousel.code !== undefined)
        .map((carousel) => [carousel.code, carousel]),
    )

    const categoryThemes: Array<ExperienceTheme> = primaryCategories
      .map((category) => {
        const carousel = categoryCarouselsByCode.get(category.parent.id)
        if (!carousel) {
          return
        }

        return {
          id: `category-${carousel.code}`,
          title: getSearchThemeTitle(carousel.code, carousel.title),
          count: countByCode.get(carousel.code) ?? 0,
          type: 'category' as const,
          categoryCode: carousel.code,
          children: category.children.map((child) => ({
            ...child,
            count: categoryFacetsById[child.id]?.count ?? 0,
          })),
        }
      })
      .filter((theme): theme is Extract<ExperienceTheme, { type: 'category' }> => !!theme)

    const collectionThemes: Array<ExperienceTheme> = globalCollections.map((collection) => ({
      id: `collection-${collection.collectionId}`,
      title: collection.theme,
      count: placeCollectionCountById.get(collection.collectionId) ?? 0,
      type: 'collection' as const,
      collectionId: collection.collectionId,
    }))

    return [...categoryThemes, ...collectionThemes]
  }, [categoryCarousels, categoryFacetsById, countByCode, globalCollections, placeCollectionCountById, primaryCategories])
}

export default useExperienceThemes
